import {
  HotelFeeConfig,
  HotelFeePolicy,
  HotelMarkupContext,
} from "@/lib/redux/features/hotel/type"

const CANCELLATION_METHODS = new Set(["ORIGINAL_CANCELLATION_FEE"])

const isFeeConfig = (value: unknown): value is HotelFeeConfig =>
  !!value &&
  typeof value === "object" &&
  "unitType" in value &&
  "amount" in value &&
  "calculationMethod" in value

/** Unwrap nested feeConfigs arrays from API (`[[config]]` or `[config]`). */
export const flattenFeeConfigs = (
  feeConfigs: HotelFeePolicy["feeConfigs"] | undefined,
): HotelFeeConfig[] => {
  if (!feeConfigs || !Array.isArray(feeConfigs)) {
    return []
  }

  const result: HotelFeeConfig[] = []
  const stack = [...feeConfigs]

  while (stack.length) {
    const item = stack.shift()
    if (!item) continue
    if (Array.isArray(item)) {
      stack.unshift(...item)
      continue
    }
    if (isFeeConfig(item)) {
      result.push(item)
    }
  }

  return result
}

export const selectHotelMarkupPolicy = (
  feePolicies: HotelFeePolicy[] | undefined,
): HotelFeePolicy | null => {
  if (!feePolicies?.length) {
    return null
  }

  const candidates = feePolicies.filter((policy) => {
    if (policy.productType !== "HOTEL" || policy.status !== "ACTIVE") {
      return false
    }
    const configs = flattenFeeConfigs(policy.feeConfigs)
    return configs.some(
      (config) => !CANCELLATION_METHODS.has(config.calculationMethod),
    )
  })

  if (!candidates.length) {
    return null
  }

  return candidates.reduce((best, current) =>
    current.priority > best.priority ? current : best,
  )
}

const getCalculationUnits = (
  method: HotelFeeConfig["calculationMethod"],
  context: HotelMarkupContext,
): number => {
  const { roomsNum, nights } = context
  switch (method) {
    case "PER_ROOM":
      return Math.max(1, roomsNum)
    case "PER_ROOM_NIGHT":
      return Math.max(1, roomsNum) * Math.max(1, nights)
    case "PER_BOOKING":
    default:
      return 1
  }
}

const applyOperation = (
  base: number,
  feeValue: number,
  operation: HotelFeeConfig["operation"],
): number => {
  switch (operation) {
    case "SUBTRACT":
      return base - feeValue
    case "MULTIPLY":
      return base * feeValue
    case "DIVIDE":
      return feeValue === 0 ? base : base / feeValue
    case "ADD":
    default:
      return base + feeValue
  }
}

export const calculateConfigMarkupAmount = (
  baseAmount: number,
  config: HotelFeeConfig,
  context: HotelMarkupContext,
): number => {
  if (CANCELLATION_METHODS.has(config.calculationMethod)) {
    return 0
  }

  const units = getCalculationUnits(config.calculationMethod, context)
  const safeBase = Math.max(0, baseAmount)

  let feeValue = 0
  if (config.unitType === "PERCENT") {
    const percentBase =
      config.calculationMethod === "PER_ROOM_NIGHT"
        ? safeBase / Math.max(1, context.roomsNum * context.nights)
        : config.calculationMethod === "PER_ROOM"
          ? safeBase / Math.max(1, context.roomsNum)
          : safeBase
    feeValue = (percentBase * config.amount) / 100 * units
  } else {
    feeValue = config.amount * units
  }

  feeValue = Math.round(feeValue)

  if (config.operation === "ADD") {
    return feeValue
  }

  return Math.round(applyOperation(safeBase, feeValue, config.operation) - safeBase)
}

export const getHotelBookingMarkup = (
  baseTotal: number,
  policy: HotelFeePolicy | null | undefined,
  context: HotelMarkupContext,
): number => {
  if (!policy || baseTotal <= 0) {
    return 0
  }

  return flattenFeeConfigs(policy.feeConfigs).reduce((total, config) => {
    if (CANCELLATION_METHODS.has(config.calculationMethod)) {
      return total
    }
    return total + calculateConfigMarkupAmount(baseTotal, config, context)
  }, 0)
}

/** Apply markup policy to a full booking base total. */
export const applyHotelMarkupToAmount = (
  baseAmount: number,
  policy: HotelFeePolicy | null | undefined,
  context: HotelMarkupContext,
): number => {
  const markup = getHotelBookingMarkup(baseAmount, policy, context)
  return Math.round(baseAmount + markup)
}

/** Convert per-night base price to customer-facing per-night display price. */
export const getHotelDisplayPrice = (
  basePerNight: number,
  policy: HotelFeePolicy | null | undefined,
  context: HotelMarkupContext,
): number => {
  if (!policy || basePerNight <= 0) {
    return basePerNight
  }

  const { roomsNum, nights } = context
  const roomNights = Math.max(1, roomsNum) * Math.max(1, nights)
  const baseTotal = basePerNight * roomNights
  const markedUpTotal = applyHotelMarkupToAmount(baseTotal, policy, context)
  return Math.round(markedUpTotal / roomNights)
}
